import { useState, useCallback } from "react";
import { BASE_URL } from "../config";
import { showError, showSuccess } from "../utils/dialog";
import StaffingRequestReplicateModal from "../components/StaffingRequestReplicateModal";

function getAuthToken() {
  return localStorage.getItem("token") || localStorage.getItem("authToken");
}

function toMysqlDateTime(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    const str = String(value || "").trim();
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(str)) return str;
    return null;
  }
  const y = parsed.getUTCFullYear();
  const m = String(parsed.getUTCMonth() + 1).padStart(2, "0");
  const d = String(parsed.getUTCDate()).padStart(2, "0");
  const hh = String(parsed.getUTCHours()).padStart(2, "0");
  const mm = String(parsed.getUTCMinutes()).padStart(2, "0");
  const ss = String(parsed.getUTCSeconds()).padStart(2, "0");
  return `${y}-${m}-${d} ${hh}:${mm}:${ss}`;
}

async function buildLegacyReplicatePreview(staffRequestId, addWeeksValue) {
  const addWeeks = Number(addWeeksValue) || 1;
  const token = getAuthToken();
  const authOpts = token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : undefined;
  const detailsRes = await fetch(
    `${BASE_URL}/api/jobs/staffingrequests/${staffRequestId}`,
    authOpts,
  );
  const detailsJson = await detailsRes.json();
  if (!detailsRes.ok) {
    throw new Error(
      detailsJson?.message || "Failed to load staffing request details",
    );
  }
  const staffRequest = detailsJson?.data || detailsJson || {};

  const shiftsRes = await fetch(
    `${BASE_URL}/api/jobs/staffingrequests/${staffRequestId}/shifts`,
    authOpts,
  );
  const shiftsJson = await shiftsRes.json();
  if (!shiftsRes.ok) {
    throw new Error(shiftsJson?.message || "Failed to load request shifts");
  }
  const shifts = Array.isArray(shiftsJson?.shifts) ? shiftsJson.shifts : [];

  const rows = shifts
    .filter((row) => String(row?.status || "").toLowerCase() !== "deleted")
    .map((row) => {
      const startDate = row?.start_date ? new Date(row.start_date) : null;
      const endDate = row?.end_date ? new Date(row.end_date) : null;
      const shiftedStart =
        startDate && !Number.isNaN(startDate.getTime())
          ? new Date(startDate.getTime() + addWeeks * 7 * 24 * 60 * 60 * 1000)
          : null;
      const shiftedEnd =
        endDate && !Number.isNaN(endDate.getTime())
          ? new Date(endDate.getTime() + addWeeks * 7 * 24 * 60 * 60 * 1000)
          : null;

      const assignedStaffName = String(row?.assigned_staff || "").trim();
      const hasAssigned = Boolean(assignedStaffName);

      return {
        shift_id: row?.shift_id,
        position_id: row?.position_id || null,
        position_name: row?.position || "",
        position_state: row?.position_state || "active",
        division_id: row?.division_id || staffRequest?.division_id || null,
        start_date: row?.start_date || null,
        end_date: row?.end_date || null,
        unpaid_break: Number(row?.unpaid_break || 0),
        replicated_start_date: shiftedStart ? shiftedStart.toISOString() : null,
        replicated_end_date: shiftedEnd ? shiftedEnd.toISOString() : null,
        assigned_account_id: null,
        assignment_state: hasAssigned ? "assigned" : null,
        assigned_staff_name: assignedStaffName,
        staff_available: true,
      };
    });

  return { staffRequest, rows };
}

function isArchivedPosition(row) {
  return String(row?.position_state || "active").toLowerCase() === "archived";
}

function buildReplicateDisplayRows(rawShifts, existingRows = []) {
  const normalized = normalizePreviewRows(rawShifts, existingRows);
  const hasArchivedPositions = normalized.some(isArchivedPosition);
  const rows = normalized.filter((row) => !isArchivedPosition(row));
  return {
    rows,
    hasArchivedPositions,
    allShiftsArchived: normalized.length > 0 && rows.length === 0,
  };
}

function normalizePreviewRows(shifts, existingRows = []) {
  const previousRowsMap = new Map(
    (existingRows || []).map((row) => [String(row.shift_id), row]),
  );
  return (shifts || []).map((shift) => {
    const previous = previousRowsMap.get(String(shift.shift_id));
    const hasAssignedStaff = Boolean(shift.assigned_account_id);
    const staffAvailable = shift.staff_available !== false;
    const includeShift =
      previous && typeof previous.includeShift !== "undefined"
        ? Boolean(previous.includeShift)
        : true;
    let includeStaffAssignment =
      previous && typeof previous.includeStaffAssignment !== "undefined"
        ? Boolean(previous.includeStaffAssignment)
        : hasAssignedStaff;
    if (!hasAssignedStaff || !staffAvailable) includeStaffAssignment = false;
    let confirmed =
      previous && typeof previous.confirmed !== "undefined"
        ? Boolean(previous.confirmed)
        : includeStaffAssignment;
    if (!includeStaffAssignment || !staffAvailable) confirmed = false;
    return {
      ...shift,
      includeShift,
      includeStaffAssignment,
      confirmed,
    };
  });
}

async function fetchReplicatePreview(
  staffRequestId,
  addWeeksValue,
  existingRows,
  weekWindow = null,
) {
  const weeks = Number(addWeeksValue) || 1;
  const token = getAuthToken();
  const hasWeekWindow = Boolean(
    weekWindow?.shiftDateFrom && weekWindow?.shiftDateTo,
  );
  const weekQuery = hasWeekWindow
    ? `&shiftDateFrom=${encodeURIComponent(weekWindow.shiftDateFrom)}&shiftDateTo=${encodeURIComponent(weekWindow.shiftDateTo)}`
    : "";
  const previewRes = await fetch(
    `${BASE_URL}/api/jobs/staffingrequests/${staffRequestId}/replicate-preview?addWeeks=${weeks}${weekQuery}`,
    {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );
  if (previewRes.status === 404) {
    const legacy = await buildLegacyReplicatePreview(staffRequestId, weeks);
    return {
      staffRequest: legacy.staffRequest || null,
      ...buildReplicateDisplayRows(legacy.rows, existingRows),
    };
  }
  const previewJson = await previewRes.json();
  if (!previewRes.ok || previewJson.success === false) {
    throw new Error(
      previewJson.message || "Failed to load staffing request replication data",
    );
  }
  const payload = previewJson?.data || {};
  let staffRequest = payload.staffRequest || null;
  let rawShifts = Array.isArray(payload.shifts) ? payload.shifts : [];

  // An empty week window is a valid result; the legacy fallback would list every week again.
  if (rawShifts.length === 0 && !hasWeekWindow) {
    const legacy = await buildLegacyReplicatePreview(staffRequestId, weeks);
    staffRequest = legacy.staffRequest || staffRequest;
    rawShifts = legacy.rows;
  }

  return {
    staffRequest,
    ...buildReplicateDisplayRows(rawShifts, existingRows),
  };
}

/**
 * Shared staffing-request replicate flow (Jobs + Schedule).
 * @param {{ onSuccess?: (result?: { newStaffRequestId?: number }) => void | Promise<void>, onOpenNewRequest?: (request: object) => void, overlayZIndex?: number }} options
 */
export default function useStaffingRequestReplicate(options = {}) {
  const { onSuccess, onOpenNewRequest, overlayZIndex = 50400 } = options;

  const [showReplicateRequestModal, setShowReplicateRequestModal] =
    useState(false);
  const [replicateSourceRequest, setReplicateSourceRequest] = useState(null);
  const [replicateRows, setReplicateRows] = useState([]);
  const [replicateWeeks, setReplicateWeeks] = useState("1");
  const [replicateLoading, setReplicateLoading] = useState(false);
  const [replicateSubmitting, setReplicateSubmitting] = useState(false);
  const [replicateHasArchivedPositions, setReplicateHasArchivedPositions] =
    useState(false);
  const [replicateAllShiftsArchived, setReplicateAllShiftsArchived] =
    useState(false);
  // Optional { shiftDateFrom, shiftDateTo } limiting the source shifts (Schedule week).
  const [replicateWeekWindow, setReplicateWeekWindow] = useState(null);

  const closeReplicateRequestModal = useCallback(() => {
    setShowReplicateRequestModal(false);
    setReplicateSourceRequest(null);
    setReplicateWeekWindow(null);
    setReplicateRows([]);
    setReplicateWeeks("1");
    setReplicateLoading(false);
    setReplicateSubmitting(false);
    setReplicateHasArchivedPositions(false);
    setReplicateAllShiftsArchived(false);
  }, []);

  const openReplicateRequestModal = useCallback(async (request, openOptions) => {
    const sourceId = Number(request?.id || request?.staff_request_id);
    if (!Number.isFinite(sourceId) || sourceId <= 0) {
      showError("Invalid staffing request");
      return;
    }
    const weekWindow =
      openOptions?.shiftDateFrom && openOptions?.shiftDateTo
        ? {
            shiftDateFrom: openOptions.shiftDateFrom,
            shiftDateTo: openOptions.shiftDateTo,
          }
        : null;
    try {
      setReplicateLoading(true);
      setShowReplicateRequestModal(true);
      setReplicateWeeks("1");
      setReplicateSourceRequest(request || null);
      setReplicateRows([]);
      setReplicateWeekWindow(weekWindow);

      const token = getAuthToken();
      const authOpts = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : undefined;

      const detailsRes = await fetch(
        `${BASE_URL}/api/jobs/staffingrequests/${sourceId}`,
        authOpts,
      );
      const detailsJson = await detailsRes.json();
      const details = detailsJson?.data || detailsJson || {};

      const previewData = await fetchReplicatePreview(
        sourceId,
        "1",
        undefined,
        weekWindow,
      );
      const mergedSource = {
        ...request,
        ...details,
        ...previewData.staffRequest,
      };

      setReplicateSourceRequest(mergedSource);
      setReplicateRows(previewData.rows);
      setReplicateHasArchivedPositions(
        Boolean(previewData.hasArchivedPositions),
      );
      setReplicateAllShiftsArchived(Boolean(previewData.allShiftsArchived));
    } catch (err) {
      console.error("Open replicate modal error:", err);
      showError(err.message || "Failed to open replicate modal");
      closeReplicateRequestModal();
    } finally {
      setReplicateLoading(false);
    }
  }, [closeReplicateRequestModal]);

  const handleReplicateWeeksChange = useCallback(
    async (nextValue) => {
      setReplicateWeeks(nextValue);
      const sourceId = Number(
        replicateSourceRequest?.staff_request_id || replicateSourceRequest?.id,
      );
      if (!Number.isFinite(sourceId) || sourceId <= 0) return;
      try {
        setReplicateLoading(true);
        const previewData = await fetchReplicatePreview(
          sourceId,
          nextValue,
          replicateRows,
          replicateWeekWindow,
        );
        setReplicateRows(previewData.rows);
        setReplicateHasArchivedPositions(
          Boolean(previewData.hasArchivedPositions),
        );
        setReplicateAllShiftsArchived(Boolean(previewData.allShiftsArchived));
      } catch (err) {
        console.error("Refresh replicate preview error:", err);
        showError(err.message || "Failed to refresh replicate dates");
      } finally {
        setReplicateLoading(false);
      }
    },
    [replicateSourceRequest, replicateRows, replicateWeekWindow],
  );

  const handleReplicateRowChange = useCallback((shiftId, field, value) => {
    const targetId = String(shiftId);
    setReplicateRows((prev) =>
      prev.map((row) => {
        if (String(row.shift_id) !== targetId) return row;
        if (field === "includeShift") {
          return { ...row, includeShift: Boolean(value) };
        }
        if (field === "includeStaffAssignment") {
          const includeStaffAssignment =
            Boolean(value) &&
            Boolean(row.assigned_account_id) &&
            row.staff_available !== false;
          return {
            ...row,
            includeStaffAssignment,
            confirmed: includeStaffAssignment ? Boolean(row.confirmed) : false,
          };
        }
        if (field === "confirmed") {
          return {
            ...row,
            confirmed: Boolean(value) && Boolean(row.includeStaffAssignment),
          };
        }
        return row;
      }),
    );
  }, []);

  const handleReplicateSubmit = useCallback(async () => {
    const sourceId = Number(
      replicateSourceRequest?.staff_request_id || replicateSourceRequest?.id,
    );
    if (!Number.isFinite(sourceId) || sourceId <= 0) {
      showError("Invalid staffing request");
      return;
    }
    const includedRows = replicateRows.filter((row) => Boolean(row.includeShift));
    if (!includedRows.length) {
      showError("Select at least one shift to replicate");
      return;
    }

    const sourceSupervisorId =
      replicateSourceRequest?.supervisor_contact_account_id != null &&
      replicateSourceRequest?.supervisor_contact_account_id !== ""
        ? Number(replicateSourceRequest.supervisor_contact_account_id)
        : null;

    try {
      setReplicateSubmitting(true);
      const token = getAuthToken();
      const res = await fetch(
        `${BASE_URL}/api/jobs/staffingrequests/${sourceId}/replicate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            addWeeks: Number(replicateWeeks) || 1,
            supervisorAccountId:
              Number.isFinite(sourceSupervisorId) && sourceSupervisorId > 0
                ? sourceSupervisorId
                : null,
            shifts: replicateRows.map((row) => ({
              shiftId: Number(row.shift_id),
              includeShift: Boolean(row.includeShift),
              includeStaffAssignment: Boolean(row.includeStaffAssignment),
              confirmed: Boolean(row.confirmed),
              assignedAccountId: row.assigned_account_id
                ? Number(row.assigned_account_id)
                : null,
            })),
          }),
        },
      );

      if (res.status === 404) {
        const source =
          replicateSourceRequest && typeof replicateSourceRequest === "object"
            ? replicateSourceRequest
            : {};
        const createRes = await fetch(`${BASE_URL}/api/jobs/staffingrequests`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            clientId: Number(source.client_id),
            locationId: source.client_location_id
              ? Number(source.client_location_id)
              : null,
            contactAccountId: source.contact_account_id
              ? Number(source.contact_account_id)
              : null,
            supervisorAccountId:
              Number.isFinite(sourceSupervisorId) && sourceSupervisorId > 0
                ? sourceSupervisorId
                : null,
            divisionId: source.division_id ? Number(source.division_id) : null,
            notes: source.note || source.notes || null,
          }),
        });
        const createJson = await createRes.json();
        if (!createRes.ok) {
          throw new Error(
            createJson?.message || "Failed to create staff request",
          );
        }
        const newRequestId = Number(
          createJson?.id || createJson?.data?.id || 0,
        );
        if (!newRequestId) {
          throw new Error("New request id was not returned");
        }

        for (const row of includedRows) {
          const startDateSql = toMysqlDateTime(row.replicated_start_date);
          const endDateSql = toMysqlDateTime(row.replicated_end_date);
          if (!startDateSql || !endDateSql) {
            throw new Error(
              `Invalid shift datetime for shift ${row.shift_id || "unknown"}`,
            );
          }
          const shiftRes = await fetch(`${BASE_URL}/api/jobs/shifts`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              staff_request_id: newRequestId,
              division_id: row.division_id || source.division_id || null,
              position_id: row.position_id || null,
              start_date: startDateSql,
              end_date: endDateSql,
              pay_rate: null,
              bill_rate: null,
            }),
          });
          if (!shiftRes.ok) {
            const shiftJson = await shiftRes.json().catch(() => ({}));
            throw new Error(
              shiftJson?.message ||
                `Failed to create replicated shift ${row.shift_id}`,
            );
          }
        }

        showSuccess("New Staff Request Created");
        closeReplicateRequestModal();
        if (onSuccess) await onSuccess({ newStaffRequestId: newRequestId });
        if (newRequestId && onOpenNewRequest) {
          try {
            const detailRes = await fetch(
              `${BASE_URL}/api/jobs/staffingrequests/${newRequestId}`,
              token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
            );
            const detailJson = await detailRes.json();
            const detailRequest = detailJson?.data || detailJson || null;
            if (detailRequest) onOpenNewRequest(detailRequest);
          } catch (err) {
            console.error("Failed to open newly created staffing request:", err);
          }
        }
        return;
      }

      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to replicate staffing request");
      }

      const newRequestId = Number(
        data?.data?.newStaffRequestId || data?.newStaffRequestId || 0,
      );
      showSuccess("New Staff Request Created");
      closeReplicateRequestModal();

      if (onSuccess) await onSuccess({ newStaffRequestId: newRequestId });
      if (newRequestId && onOpenNewRequest) {
        try {
          const detailRes = await fetch(
            `${BASE_URL}/api/jobs/staffingrequests/${newRequestId}`,
            token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
          );
          const detailJson = await detailRes.json();
          const detailRequest = detailJson?.data || detailJson || null;
          if (detailRequest) onOpenNewRequest(detailRequest);
        } catch (err) {
          console.error("Failed to open newly created staffing request:", err);
        }
      }
    } catch (err) {
      console.error("Replicate staffing request error:", err);
      showError(err.message || "Failed to replicate staffing request");
    } finally {
      setReplicateSubmitting(false);
    }
  }, [
    replicateSourceRequest,
    replicateRows,
    replicateWeeks,
    closeReplicateRequestModal,
    onSuccess,
    onOpenNewRequest,
  ]);

  const ReplicateModal = (
    <StaffingRequestReplicateModal
      open={showReplicateRequestModal}
      onClose={closeReplicateRequestModal}
      replicateSourceRequest={replicateSourceRequest}
      replicateRows={replicateRows}
      replicateWeeks={replicateWeeks}
      replicateLoading={replicateLoading}
      replicateSubmitting={replicateSubmitting}
      hasArchivedPositions={replicateHasArchivedPositions}
      allShiftsArchived={replicateAllShiftsArchived}
      onWeeksChange={handleReplicateWeeksChange}
      onRowChange={handleReplicateRowChange}
      onSubmit={handleReplicateSubmit}
      overlayZIndex={overlayZIndex}
    />
  );

  return {
    openReplicateRequestModal,
    closeReplicateRequestModal,
    ReplicateModal,
  };
}
